import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

const emptyFlavor = { name: "", description: "", color: "#D4B989", available: true };

export default function FlavorsManager() {
  const [flavors, setFlavors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyFlavor);

  const load = () => api.get("/admin/flavors").then((res) => setFlavors(res.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(emptyFlavor); setOpen(true); };
  const openEdit = (f) => { setEditing(f); setForm({ name: f.name, description: f.description, color: f.color, available: f.available }); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    try {
      if (editing) await api.put(`/admin/flavors/${editing.id}`, form);
      else await api.post("/admin/flavors", form);
      toast.success("Flavor saved");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this flavor?")) return;
    await api.delete(`/admin/flavors/${id}`);
    toast.success("Flavor removed");
    load();
  };

  return (
    <div data-testid="admin-flavors">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif-display text-3xl text-sc-espresso">Gelato Flavors</h2>
          <p className="text-sm text-sc-espresso/60 mt-1">Manage the flavors shown on the cart.</p>
        </div>
        <button onClick={openAdd} data-testid="add-flavor-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sc-espresso text-sc-cream text-sm font-medium hover:bg-sc-gold transition-colors">
          <Plus size={18} /> Add Flavor
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {flavors.map((f) => (
          <div key={f.id} className="bg-white rounded-2xl p-5 border border-sc-gold/15 shadow-sm" data-testid={`flavor-card-${f.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full border border-black/10" style={{ background: f.color }} />
                <h3 className="font-serif-display text-xl text-sc-espresso">{f.name}</h3>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(f)} className="p-2 rounded-lg hover:bg-sc-blue/40 text-sc-espresso/70"><Pencil size={16} /></button>
                <button onClick={() => remove(f.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
              </div>
            </div>
            <p className="mt-2 text-sm text-sc-espresso/60">{f.description}</p>
            <span className={`mt-3 inline-block text-xs px-2 py-1 rounded-full ${f.available ? "bg-sc-sage/20 text-sc-espresso" : "bg-gray-100 text-gray-400"}`}>
              {f.available ? "Available" : "Hidden"}
            </span>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-sc-cream">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">{editing ? "Edit Flavor" : "Add Flavor"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <Field label="Name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="flavor-name-input" className={inputCls} /></Field>
            <Field label="Description"><textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls + " resize-none"} /></Field>
            <div className="flex items-center gap-6">
              <Field label="Color">
                <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-16 h-10 rounded cursor-pointer bg-transparent" />
              </Field>
              <label className="flex items-center gap-2 mt-5 text-sm text-sc-espresso cursor-pointer">
                <input type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} /> Available on cart
              </label>
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} data-testid="save-flavor-btn" className="px-6 py-2.5 rounded-full bg-sc-espresso text-sc-cream font-medium hover:bg-sc-gold transition-colors">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const inputCls = "w-full bg-white border border-sc-gold/25 rounded-xl px-4 py-2.5 text-sc-espresso focus:outline-none focus:border-sc-gold";
function Field({ label, children }) {
  return (<div><label className="eyebrow block mb-1.5">{label}</label>{children}</div>);
}
